/*
 * /map-chart-maker — карта с выделенными странами и регионами.
 * Движок — engine.js (геометрия, проекции, подписи, классы, цвета); данные —
 * public/data/true-size (границы по домену) и public/data/map-studio/meta.json.
 *
 * Два способа раскрасить:
 *   groups — группы цветов, нажатие по стране кладёт её в выбранную группу;
 *   data   — вставленная таблица «страна → число», классы и цветовая шкала.
 * Работа живёт в браузере (__toolDraft.editor), делится ссылкой (lz-string в
 * #m=…), экспорт — PNG (потолок размера по тарифу), SVG и PDF.
 */
import * as E from './engine.js';
import { rasterizeSvg } from './raster.js';

const SLUG = 'map-chart-maker';
const W = window.__MAPCHART || {};
const I = W.i18n || {};
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const tr = (k, rep = {}) => String(I[k] != null ? I[k] : k).replace(/:([a-z_]+)/g, (m, n) => (rep[n] != null ? rep[n] : m));
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const LOC = (window.__intlLocale && window.__intlLocale()) || 'en';
const LANG = W.locale || 'en';
const d3 = window.d3;
function ae(action, extra) { try { window.__ae && window.__ae('feature_use', Object.assign({ slug: SLUG, action }, extra || {})); } catch (e) { /* optional */ } }
// The funnel (tool_stage; the last stage rides on tool_abandon): ready → painting → export → complete.
function stage(name, meta) { try { window.__stage && window.__stage(name, meta || {}); } catch (e) { /* optional */ } }
function diag(issue, extra) { try { window.__aeDiagnostic && window.__aeDiagnostic(Object.assign({ issue }, extra || {})); } catch (e) { /* optional */ } }
let editing = false;
function firstEdit(how) { if (editing) return; editing = true; stage('editing', { how }); }
function toast(m, t) { try { window.__toast && window.__toast(m, t || 'info'); } catch (e) { /* optional */ } }
const nf = new Intl.NumberFormat(LOC, { maximumFractionDigits: 2 });
const nfc = (() => { try { return new Intl.NumberFormat(LOC, { notation: 'compact', maximumFractionDigits: 1 }); } catch (e) { return nf; } })();
const fmtNum = (x) => (Math.abs(x) >= 1e4 ? nfc.format(x) : nf.format(x));

// ── views ────────────────────────────────────────────────────────────────
/* Части света — азимутальная равновеликая с центром в середине материка:
   площади не врут, и материк не загибается к краям. Рамка — прямоугольник
   координат, страны за ним обрезаются краем карты, а не вычёркиваются. */
const VIEWS = {
    world: { proj: 'equalEarth', rotate: [-10, 0], sphere: true, projs: ['equalEarth', 'naturalEarth', 'robinson', 'winkel3', 'mollweide', 'miller', 'mercator', 'orthographic'] },
    EU: { proj: 'azimuthalEqualArea', rotate: [-15, -52], box: [-25, 34, 45, 71] },
    AS: { proj: 'azimuthalEqualArea', rotate: [-95, -40], box: [26, -11, 150, 78] },
    AF: { proj: 'azimuthalEqualArea', rotate: [-18, -2], box: [-19, -36, 52, 38] },
    NA: { proj: 'azimuthalEqualArea', rotate: [100, -45], box: [-168, 7, -52, 80] },
    SA: { proj: 'azimuthalEqualArea', rotate: [60, 20], box: [-83, -56, -34, 13] },
    OC: { proj: 'azimuthalEqualArea', rotate: [-150, 25], box: [110, -48, 180, 0] },
};
const CONT_PROJS = ['azimuthalEqualArea', 'conicEqualArea', 'mercator', 'orthographic'];

/* Ready-made sets of countries — what people colour a map for most often. Members as of
   October 2026 (the hint under the list says so): ASEAN with Timor-Leste (admitted 26.10.2025),
   the eurozone with Bulgaria (01.01.2026), BRICS with Indonesia (01.2025), SCO with Belarus
   (07.2024), OPEC without Angola (left 01.2024). Keys are the map's own ISO-3 codes. */
const SETS = {
    eu: 'AUT BEL BGR HRV CYP CZE DNK EST FIN FRA DEU GRC HUN IRL ITA LVA LTU LUX MLT NLD POL PRT ROU SVK SVN ESP SWE',
    euro: 'AUT BEL BGR HRV CYP EST FIN FRA DEU GRC IRL ITA LVA LTU LUX MLT NLD PRT SVK SVN ESP',
    schengen: 'AUT BEL BGR HRV CZE DNK EST FIN FRA DEU GRC HUN ISL ITA LVA LIE LTU LUX MLT NLD NOR POL PRT ROU SVK SVN ESP SWE CHE',
    nato: 'ALB BEL BGR CAN HRV CZE DNK EST FIN FRA DEU GRC HUN ISL ITA LVA LTU LUX MNE NLD MKD NOR POL PRT ROU SVK SVN ESP SWE TUR GBR USA',
    eaeu: 'ARM BLR KAZ KGZ RUS',
    brics: 'BRA RUS IND CHN ZAF EGY ETH IRN ARE IDN',
    sco: 'CHN RUS IND PAK KAZ KGZ TJK UZB IRN BLR',
    g7: 'CAN FRA DEU ITA JPN GBR USA',
    g20: 'ARG AUS BRA CAN CHN FRA DEU IND IDN ITA JPN MEX RUS SAU ZAF KOR TUR GBR USA',
    opec: 'DZA COG GNQ GAB IRN IRQ KWT LBY NGA SAU ARE VEN',
    asean: 'BRN KHM IDN LAO MYS MMR PHL SGP THA VNM TLS',
    arab: 'DZA BHR COM DJI EGY IRQ JOR KWT LBN LBY MRT MAR OMN PSX QAT SAU SOM SDN SYR TUN ARE YEM',
    p5: 'CHN FRA RUS GBR USA',
};
// The names come with the page (the same words as the list in the rail).
const setName = (k) => (W.sets && W.sets[k]) || k;
// A browser without a colour picker shows the hex as text: then the swatch is the field's own background.
const COLOR_OK = (() => { try { const i = document.createElement('input'); i.type = 'color'; return i.type === 'color'; } catch (e) { return false; } })();
const REGION_PROJS = ['conicEqualArea', 'conicEquidistant', 'mercator', 'azimuthalEqualArea'];

// ── state ────────────────────────────────────────────────────────────────
const DEF_GROUPS = () => E.QUALITATIVE.slice(0, 4).map((c, i) => ({ c, l: tr('group_n', { n: i + 1 }) }));
function freshState() {
    return {
        v: 2, view: 'world', proj: '', mode: 'groups', active: 0,
        groups: DEF_GROUPS(), assign: {},
        data: { text: '', method: 'quantile', k: 5, ramp: 'blues', rev: false },
        style: { title: '', note: '', legendTitle: '', labels: false, grat: false, ocean: '#dbeafe', land: '#e5e7eb', border: '#ffffff', legend: 'below' },
    };
}
let S = freshState();
let world = null, regions = {}, feats = [], byId = new Map(), matcher = null;
let geoCache = { key: '', paths: new Map(), H: 0, proj: null, sphere: '', grat: '' };
const VB_W = 1000;
const hist = { stack: [], pos: -1, lock: false };
let drafts = null;

function snapshot() { return JSON.stringify(S); }
function commit(counted = true) {
    if (hist.lock) return;
    const s = snapshot();
    if (hist.stack[hist.pos] === s) return;
    hist.stack.splice(hist.pos + 1);
    hist.stack.push(s);
    if (hist.stack.length > 120) hist.stack.shift();
    hist.pos = hist.stack.length - 1;
    syncUndo();
    if (drafts) drafts.touch(counted);
}
function restore(s) {
    hist.lock = true;
    try { S = Object.assign(freshState(), JSON.parse(s)); } finally { hist.lock = false; }
    syncControls();
    loadView().then((fresh) => { if (fresh) render(); });
}
function undo() { if (hist.pos > 0) { hist.pos--; restore(hist.stack[hist.pos]); syncUndo(); if (drafts) drafts.touch(true); } }
function redo() { if (hist.pos < hist.stack.length - 1) { hist.pos++; restore(hist.stack[hist.pos]); syncUndo(); if (drafts) drafts.touch(true); } }
function syncUndo() { $('#mc-undo').disabled = hist.pos <= 0; $('#mc-redo').disabled = hist.pos >= hist.stack.length - 1; }

// ── data loading ─────────────────────────────────────────────────────────
async function getJson(url) {
    const r = await fetch(url, { credentials: 'omit' });
    if (!r.ok) throw new Error('HTTP ' + r.status + ' ' + url);
    return r.json();
}
async function loadWorld() {
    if (world) return world;
    world = E.fixWinding(E.decode(await getJson(W.worldUrl)), d3);
    return world;
}
async function loadRegions(a3) {
    if (regions[a3]) return regions[a3];
    const url = W.regionUrl.replace('__A3__', W.povSplit && W.povSplit.includes(a3) ? `${a3}-${W.pov}` : a3);
    regions[a3] = E.fixWinding(E.decode(await getJson(url)), d3);
    return regions[a3];
}
/* The last choice of map wins. Regions arrive over the network; a person who taps «Regions» and
   then «World» before they came got the regions painted under a «World» chip (and the world's
   names matched against them). A load that is no longer the latest returns false and changes nothing. */
let loadSeq = 0;
async function loadView() {
    const seq = ++loadSeq, view = S.view;
    const busy = $('#mc-busy');
    busy.hidden = false;
    try {
        const f = view.startsWith('reg:') ? await loadRegions(view.slice(4)) : await loadWorld();
        if (seq !== loadSeq) return false;
        feats = f;
        byId = new Map(feats.map((x) => [x.id, x]));
        matcher = E.buildMatcher(feats, view.startsWith('reg:') ? {} : (W.aliases || {}));
        geoCache.key = '';
        return true;
    } catch (e) {
        if (seq !== loadSeq) return false;
        try { window.__handleError && window.__handleError(e, 'map_load', { say: tr('load_fail') }); } catch (_) { toast(tr('load_fail'), 'error'); }
        return false;
    } finally { if (seq === loadSeq) busy.hidden = true; }
}

// ── geometry for the current view ────────────────────────────────────────
function viewSpec() {
    if (S.view.startsWith('reg:')) {
        const a3 = S.view.slice(4);
        if (a3 === 'USA' && (!S.proj || S.proj === 'albersUsa')) return { proj: 'albersUsa', fitFeatures: true, projs: ['albersUsa', ...REGION_PROJS] };
        // A conic fitted to the country: centre meridian and standard parallels at 1/6 and 5/6 of its latitudes.
        const b = d3.geoBounds({ type: 'FeatureCollection', features: feats });
        let lon0 = (b[0][0] + b[1][0]) / 2;
        if (b[1][0] < b[0][0]) lon0 = E.wrapLon((b[0][0] + b[1][0] + 360) / 2);
        if (a3 === 'RUS') lon0 = 100;
        const la0 = b[0][1], la1 = b[1][1], span = la1 - la0;
        return { proj: S.proj || 'conicEqualArea', rotate: [-lon0, 0], parallels: [la0 + span / 6, la1 - span / 6], fitFeatures: true, projs: REGION_PROJS, center: [lon0, (la0 + la1) / 2] };
    }
    const v = VIEWS[S.view] || VIEWS.world;
    return Object.assign({}, v, { proj: S.proj || v.proj, projs: v.projs || CONT_PROJS });
}
function boxPolygon([x0, y0, x1, y1]) {
    // A densified lon/lat rectangle: its straight edges curve in the projection like meridians do.
    const pts = [];
    for (let i = 0; i <= 20; i++) pts.push([x0 + (x1 - x0) * i / 20, y0]);
    for (let i = 0; i <= 20; i++) pts.push([x1, y0 + (y1 - y0) * i / 20]);
    for (let i = 20; i >= 0; i--) pts.push([x0 + (x1 - x0) * i / 20, y1]);
    for (let i = 20; i >= 0; i--) pts.push([x0, y0 + (y1 - y0) * i / 20]);
    return { type: 'Feature', geometry: { type: 'MultiPoint', coordinates: pts } };
}
function geometry() {
    const spec = viewSpec();
    const key = [S.view, spec.proj, feats.length].join('|');
    if (geoCache.key === key) return geoCache;
    const p = E.makeProjection({ id: spec.proj, rotate: spec.proj === 'orthographic' ? [-(spec.center ? spec.center[0] : 30), -(spec.center ? spec.center[1] : 20), 0] : spec.rotate, parallels: spec.parallels }, d3);
    let target;
    if (spec.fitFeatures) target = { type: 'FeatureCollection', features: feats };
    else if (spec.box && spec.proj !== 'orthographic') target = boxPolygon(spec.box);
    else target = { type: 'Sphere' };
    p.fitWidth(VB_W - 16, target);
    const path = d3.geoPath(p);
    const bb = path.bounds(target);
    let H = Math.ceil(bb[1][1] - bb[0][1] + 16);
    H = Math.max(260, Math.min(H, 1100));
    p.translate([p.translate()[0] + 8 - bb[0][0], p.translate()[1] + 8 - bb[0][1]]);
    if (!spec.sphere && p.clipExtent) p.clipExtent([[0, 0], [VB_W, H]]);
    const pathF = d3.geoPath(p);
    const paths = new Map();
    for (const f of feats) {
        const d = pathF(f);
        if (d) paths.set(f.id, d);
    }
    geoCache = {
        key, paths, H, proj: p, spec,
        sphere: spec.sphere || spec.proj === 'orthographic' ? pathF({ type: 'Sphere' }) : '',
        grat: pathF(d3.geoGraticule10()),
    };
    return geoCache;
}

// ── colours ──────────────────────────────────────────────────────────────
let dataView = null;     // {values: Map(id→v), breaks, colors, unmatched:[names]}
function computeData() {
    const t = E.parseTable(S.data.text);
    const values = new Map(), unmatched = [];
    for (const r of t.rows) {
        const id = matcher ? matcher(r.name) : null;
        if (id && byId.has(id)) values.set(id, r.value); else unmatched.push(r.name);
    }
    const breaks = E.classify([...values.values()], S.data.method, S.data.k);
    let colors = E.ramp(S.data.ramp, breaks.length + 1);
    if (S.data.rev) colors = colors.reverse();
    dataView = { values, breaks, colors, unmatched, bad: t.bad, rows: t.rows.length };
    reportUnmatched(dataView);
    return dataView;
}
/* Names the matcher did not know are the list of aliases to add (aliases.json). Sent once per
   table, after typing settles: the names only, never the numbers. */
let unmatchedSent = '';
function reportUnmatched(dv) {
    if (!dv.unmatched.length) return;
    const key = dv.unmatched.join('|');
    if (key === unmatchedSent) return;
    clearTimeout(reportUnmatched.t);
    reportUnmatched.t = setTimeout(() => {
        unmatchedSent = key;
        diag('data_unmatched', { n: dv.unmatched.length, of: dv.rows, names: dv.unmatched.slice(0, 10).join('; ').slice(0, 300), view: S.view.startsWith('reg:') ? 'reg' : S.view });
    }, 4000);
}
function fillOf(id) {
    if (S.mode === 'data') {
        const dv = dataView;
        if (!dv || !dv.values.has(id)) return S.style.land;
        return dv.colors[E.classOf(dv.values.get(id), dv.breaks)];
    }
    const g = S.assign[id];
    return g != null && S.groups[g] ? S.groups[g].c : S.style.land;
}

// ── render ───────────────────────────────────────────────────────────────
const svgNS = 'http://www.w3.org/2000/svg';
let measureCtx = null;
function textWidth(text, size, weight = 600) {
    if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
    measureCtx.font = `${weight} ${size}px system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`;
    return measureCtx.measureText(text).width;
}
function legendItems() {
    if (S.mode === 'data') {
        const dv = dataView;
        if (!dv || !dv.values.size) return [];
        const lo = Math.min(...dv.values.values()), hi = Math.max(...dv.values.values());
        const edges = [lo, ...dv.breaks, hi];
        return dv.colors.map((c, i) => ({ c, l: i === dv.colors.length - 1 ? `${fmtNum(edges[i])} – ${fmtNum(edges[i + 1])}` : `${fmtNum(edges[i])} – < ${fmtNum(edges[i + 1])}` }));
    }
    // Only groups that colour something on THIS map: a world colouring stays in the
    // state when the view switches to regions, and its groups must not haunt the legend.
    const used = new Set(Object.entries(S.assign).filter(([id]) => byId.has(id)).map(([, g]) => g));
    return S.groups.map((g, i) => ({ c: g.c, l: g.l, i })).filter((g) => used.has(g.i) && g.l.trim());
}
function render() {
    if (!feats.length) return;
    if (S.mode === 'data') computeData();
    const g = geometry();
    const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif';
    const top = S.style.title ? 52 : 0;
    // Legend below the map: rows of swatches that wrap to the width.
    const items = S.style.legend === 'none' ? [] : legendItems();
    const legRows = [];
    if (items.length) {
        let row = [], x = 0;
        for (const it of items) {
            const w = 22 + textWidth(it.l, 15, 500) + 22;
            if (x + w > VB_W - 24 && row.length) { legRows.push(row); row = []; x = 0; }
            row.push(Object.assign({ x, w }, it)); x += w;
        }
        if (row.length) legRows.push(row);
    }
    const legTitleH = items.length && S.style.legendTitle ? 24 : 0;
    const legH = items.length ? legTitleH + legRows.length * 26 + 14 : 0;
    const noteH = S.style.note ? 26 : 0;
    const H = top + g.H + legH + noteH;
    const svg = $('#mc-svg');
    svg.setAttribute('viewBox', `0 0 ${VB_W} ${H}`);
    const parts = [];
    parts.push(`<rect width="${VB_W}" height="${H}" fill="#ffffff"/>`);
    if (S.style.title) parts.push(`<text x="${VB_W / 2}" y="36" text-anchor="middle" font-family='${FONT}' font-size="28" font-weight="700" fill="#111827">${esc(S.style.title)}</text>`);
    parts.push(`<g transform="translate(0 ${top})"><g id="mc-zoom">`);
    if (g.sphere) parts.push(`<path d="${g.sphere}" fill="${S.style.ocean}" stroke="#94a3b8" stroke-width="1"/>`);
    else parts.push(`<rect width="${VB_W}" height="${g.H}" fill="${S.style.ocean}"/>`);
    if (S.style.grat) parts.push(`<path d="${g.grat}" fill="none" stroke="#94a3b8" stroke-opacity=".45" stroke-width=".6"/>`);
    parts.push('<g class="mc-c" stroke-linejoin="round">');
    for (const f of feats) {
        const d = g.paths.get(f.id);
        if (!d) continue;
        parts.push(`<path data-id="${esc(f.id)}" d="${d}" fill="${fillOf(f.id)}" stroke="${S.style.border}" stroke-width=".6"><title>${esc(E.nameOf(f, LANG))}${valueNote(f.id)}</title></path>`);
    }
    parts.push('</g>');
    if (S.style.labels) parts.push(labelsSvg(g, FONT));
    parts.push('</g></g>');
    if (items.length) {
        let y = top + g.H + 14;
        if (S.style.legendTitle) { parts.push(`<text x="24" y="${y + 14}" font-family='${FONT}' font-size="16" font-weight="700" fill="#111827">${esc(S.style.legendTitle)}</text>`); y += legTitleH; }
        for (const row of legRows) {
            for (const it of row) parts.push(`<rect x="${24 + it.x}" y="${y + 3}" width="16" height="16" rx="3" fill="${it.c}" stroke="#00000026"/><text x="${24 + it.x + 22}" y="${y + 16}" font-family='${FONT}' font-size="15" font-weight="500" fill="#1f2937">${esc(it.l)}</text>`);
            y += 26;
        }
    }
    if (S.style.note) parts.push(`<text x="24" y="${H - 9}" font-family='${FONT}' font-size="13" fill="#6b7280">${esc(S.style.note)}</text>`);
    svg.innerHTML = parts.join('');
    applyZoom();
    renderSide();
}
function valueNote(id) {
    if (S.mode === 'data' && dataView && dataView.values.has(id)) return ': ' + fmtNum(dataView.values.get(id));
    if (S.mode === 'groups' && S.assign[id] != null && S.groups[S.assign[id]]) return ' — ' + S.groups[S.assign[id]].l;
    return '';
}
function labelsSvg(g, FONT) {
    const cands = [];
    for (const f of feats) {
        if (!g.paths.has(f.id)) continue;
        const s = E.labelSpot(f, g.proj);
        if (!s || s.x < 0 || s.x > VB_W || s.y < 0 || s.y > g.H) continue;
        const size = Math.max(9, Math.min(17, Math.sqrt(s.area) / 9));
        cands.push({ x: s.x, y: s.y, room: s.room, text: E.nameOf(f, LANG), size, priority: s.area, fill: fillOf(f.id) });
    }
    const placed = E.placeLabels(cands, (t, sz) => textWidth(t, sz), { fitInside: true, bounds: [0, 0, VB_W, g.H] });
    return '<g pointer-events="none">' + placed.map((p) => {
        const ink = E.inkOn(p.fill);
        const halo = ink === '#ffffff' ? '#00000055' : '#ffffffcc';
        return `<text x="${p.x.toFixed(1)}" y="${(p.y + p.size * 0.35).toFixed(1)}" text-anchor="middle" font-family='${FONT}' font-size="${p.size.toFixed(1)}" font-weight="600" fill="${ink}" stroke="${halo}" stroke-width="2.5" paint-order="stroke">${esc(p.text)}</text>`;
    }).join('') + '</g>';
}

// side panel: groups list, data report
function renderSide() {
    if (S.mode === 'groups') {
        const counts = S.groups.map(() => 0);
        for (const [id, g] of Object.entries(S.assign)) if (counts[g] != null && byId.has(id)) counts[g]++;
        // The colour input IS the swatch; the row (or its name field) picks the group to paint with.
        $('#mc-groups').innerHTML = S.groups.map((g, i) => `<div class="mc-grp${i === S.active ? ' on' : ''}" data-i="${i}" role="radio" aria-checked="${i === S.active}" tabindex="-1">
            <input type="color" class="mc-grp-color" data-color="${i}" value="${g.c}" aria-label="${esc(tr('group_color'))}"${COLOR_OK ? '' : ` style="background:${g.c};color:transparent"`}>
            <input class="tc-in mc-grp-label" data-label="${i}" value="${esc(g.l)}" aria-label="${esc(tr('group_label'))}">
            <span class="mc-grp-n" title="${esc(tr('group_count'))}">${counts[i]}</span>
            <button type="button" class="mc-grp-x" data-del="${i}" aria-label="${esc(tr('group_del'))}" ${S.groups.length < 2 ? 'disabled' : ''}>×</button>
        </div>`).join('');
        renderQuick(counts);
    } else {
        renderQuick(null);
        const dv = dataView;
        const rep = $('#mc-data-report');
        if (!dv || !dv.rows) { rep.innerHTML = ''; return; }
        const bits = [`<p class="tc-msg good">${esc(tr('data_matched', { n: dv.values.size, of: dv.rows }))}</p>`];
        if (dv.unmatched.length) bits.push(`<p class="tc-msg warn">${esc(tr('data_unmatched', { list: dv.unmatched.slice(0, 12).join(', ') + (dv.unmatched.length > 12 ? '…' : '') }))}</p>`);
        if (dv.bad.length) bits.push(`<p class="tc-msg">${esc(tr('data_bad', { n: dv.bad.length }))}</p>`);
        rep.innerHTML = bits.join('');
    }
}

/* The colour a tap paints with, next to the map: on a phone the group list is a screen away,
   and switching colours meant scrolling there and back for every country. */
function renderQuick(counts) {
    const q = $('#mc-quick');
    if (!q) return;
    q.hidden = !counts;
    if (!counts) { q.innerHTML = ''; return; }
    q.innerHTML = S.groups.map((g, i) => `<button type="button" class="mc-q${i === S.active ? ' on' : ''}" data-q="${i}" role="radio" aria-checked="${i === S.active}" aria-label="${esc(g.l || tr('group_n', { n: i + 1 }))}" title="${esc(g.l)}"><span style="background:${g.c}"></span>${counts[i] ? `<b>${counts[i]}</b>` : ''}</button>`).join('');
}
function setActive(i) {
    S.active = i;
    $$('.mc-grp').forEach((r) => { const on = +r.dataset.i === S.active; r.classList.toggle('on', on); r.setAttribute('aria-checked', on ? 'true' : 'false'); });
    $$('.mc-q').forEach((b) => { const on = +b.dataset.q === S.active; b.classList.toggle('on', on); b.setAttribute('aria-checked', on ? 'true' : 'false'); });
}
function applySet(key) {
    const ids = (SETS[key] || '').split(' ').filter((id) => byId.has(id) && geoCache.paths.has(id));
    if (!ids.length) { toast(tr('set_none'), 'info'); return; }
    for (const id of ids) S.assign[id] = S.active;
    // A group still called «Group N» takes the set's name — that is what the legend should say.
    const g = S.groups[S.active];
    if (g && (!g.l.trim() || g.l === tr('group_n', { n: S.active + 1 }))) g.l = setName(key);
    render(); commit();
    toast(tr('set_done', { n: ids.length, set: setName(key) }), 'success');
    ae('set', { set: key, n: ids.length });
    firstEdit('set');
}

// ── zoom & pan (screen only; exports are always the whole map) ─────────────
const Z = { k: 1, x: 0, y: 0 };
function applyZoom() {
    const g = $('#mc-zoom');
    if (g) g.setAttribute('transform', `translate(${Z.x} ${Z.y}) scale(${Z.k})`);
    const c = $('.mc-c');
    if (c) c.setAttribute('stroke-width', String(0.6 / Z.k));
    // At full view a finger on the map must still scroll the page (the map fills a phone
    // screen); once zoomed in, one finger pans the map instead.
    const svg = $('#mc-svg');
    if (svg) svg.style.touchAction = Z.k > 1 ? 'none' : 'pan-y';
    const back = $('#mc-zreset');
    if (back) back.hidden = Z.k === 1;
}
function zoomAt(f, cx, cy) {
    const k = Math.max(1, Math.min(24, Z.k * f));
    Z.x = cx - (cx - Z.x) * (k / Z.k); Z.y = cy - (cy - Z.y) * (k / Z.k); Z.k = k;
    if (Z.k === 1) { Z.x = 0; Z.y = 0; }
    applyZoom();
}
function svgPoint(evt) {
    const svg = $('#mc-svg'), r = svg.getBoundingClientRect(), vb = svg.viewBox.baseVal;
    const top = S.style.title ? 52 : 0;
    return [(evt.clientX - r.left) * vb.width / r.width, (evt.clientY - r.top) * vb.height / r.height - top];
}
function bindZoom() {
    const svg = $('#mc-svg');
    svg.addEventListener('wheel', (e) => { if (!e.ctrlKey && !e.metaKey && Z.k === 1 && e.deltaY > 0) return; e.preventDefault(); const [x, y] = svgPoint(e); zoomAt(e.deltaY < 0 ? 1.25 : 0.8, x, y); }, { passive: false });
    const ptrs = new Map();
    let start = null, moved = false, pinch0 = 0;
    svg.addEventListener('pointerdown', (e) => {
        ptrs.set(e.pointerId, [e.clientX, e.clientY]);
        if (ptrs.size === 1) { start = { x: e.clientX, y: e.clientY, zx: Z.x, zy: Z.y }; moved = false; }
        if (ptrs.size === 2) { const [a, b] = [...ptrs.values()]; pinch0 = Math.hypot(a[0] - b[0], a[1] - b[1]); }
    });
    svg.addEventListener('pointermove', (e) => {
        if (!ptrs.has(e.pointerId)) return;
        ptrs.set(e.pointerId, [e.clientX, e.clientY]);
        const r = svg.getBoundingClientRect(), sc = svg.viewBox.baseVal.width / r.width;
        if (ptrs.size === 2) {
            const [a, b] = [...ptrs.values()], d = Math.hypot(a[0] - b[0], a[1] - b[1]);
            if (pinch0 > 0) { const top = S.style.title ? 52 : 0; zoomAt(d / pinch0, ((a[0] + b[0]) / 2 - r.left) * sc, ((a[1] + b[1]) / 2 - r.top) * sc - top); pinch0 = d; moved = true; }
            return;
        }
        if (!start || Z.k === 1) return;
        const dx = e.clientX - start.x, dy = e.clientY - start.y;
        if (Math.hypot(dx, dy) > 6) moved = true;
        if (moved) { Z.x = start.zx + dx * sc; Z.y = start.zy + dy * sc; applyZoom(); }
    });
    const end = (e) => { ptrs.delete(e.pointerId); if (ptrs.size < 2) pinch0 = 0; };
    svg.addEventListener('pointerup', end); svg.addEventListener('pointercancel', end);
    svg.addEventListener('click', (e) => {
        if (moved) { moved = false; return; }
        const p = e.target.closest && e.target.closest('path[data-id]');
        if (p) onCountry(p.getAttribute('data-id'));
    });
    $('#mc-zin').addEventListener('click', () => zoomAt(1.5, VB_W / 2, geoCache.H / 2));
    $('#mc-zout').addEventListener('click', () => zoomAt(1 / 1.5, VB_W / 2, geoCache.H / 2));
    $('#mc-zreset').addEventListener('click', () => { Z.k = 1; Z.x = 0; Z.y = 0; applyZoom(); });
    if (window.__wholeScreen) {
        const ws = window.__wholeScreen($('.mc-stage'), { key: SLUG, onChange: (on) => { $('#mc-full').setAttribute('aria-pressed', on ? 'true' : 'false'); ae('whole_screen', { on: on ? 1 : 0 }); } });
        $('#mc-full').hidden = false;
        $('#mc-full').addEventListener('click', () => ws.toggle());
    }
}

// ── interactions ─────────────────────────────────────────────────────────
function onCountry(id) {
    if (S.mode !== 'groups') { toast(tr('data_mode_click'), 'info'); return; }
    if (S.assign[id] === S.active) delete S.assign[id]; else S.assign[id] = S.active;
    commit();
    paint(id);
    renderSide();
    ae('paint');
    firstEdit('paint');
}
function paint(id) {
    const p = $(`#mc-svg path[data-id="${CSS.escape(id)}"]`);
    if (p) p.setAttribute('fill', fillOf(id));
    if (S.style.labels || S.style.legend !== 'none') render();
}

function setView(v) {
    S.view = v; S.proj = '';
    Z.k = 1; Z.x = 0; Z.y = 0;
    syncControls();
    loadView().then((fresh) => { if (!fresh) return; syncProjOptions(); render(); commit(); });
    ae('view', { view: v.startsWith('reg:') ? 'reg' : v });
}
function syncProjOptions() {
    const spec = viewSpec(), sel = $('#mc-proj');
    sel.innerHTML = spec.projs.map((p) => `<option value="${p}"${p === spec.proj ? ' selected' : ''}>${esc(tr('proj_' + p))}</option>`).join('');
}
function syncControls() {
    $$('.mc-views .tc-chip').forEach((b) => { const on = b.dataset.view === (S.view.startsWith('reg:') ? 'reg' : S.view); b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $('#mc-reg-wrap').hidden = !S.view.startsWith('reg:');
    $('#mc-set-wrap').hidden = S.view.startsWith('reg:');
    if (S.view.startsWith('reg:')) $('#mc-reg').value = S.view.slice(4);
    $$('.mc-modes .tc-seg-b').forEach((b) => { const on = b.dataset.mode === S.mode; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $('#mc-pane-groups').hidden = S.mode !== 'groups';
    $('#mc-pane-data').hidden = S.mode !== 'data';
    $('#mc-data-text').value = S.data.text;
    $('#mc-method').value = S.data.method;
    $('#mc-k').value = String(S.data.k);
    $('#mc-rev').checked = !!S.data.rev;
    $$('.mc-ramp').forEach((b) => { const on = b.dataset.ramp === S.data.ramp; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $('#mc-title').value = S.style.title; $('#mc-note').value = S.style.note; $('#mc-legend-title').value = S.style.legendTitle;
    $('#mc-labels').checked = !!S.style.labels; $('#mc-grat').checked = !!S.style.grat;
    $('#mc-ocean').value = S.style.ocean; $('#mc-land').value = S.style.land; $('#mc-border').value = S.style.border;
    $('#mc-legend').value = S.style.legend;
    if (feats.length) syncProjOptions();
}

// ── share link, drafts ───────────────────────────────────────────────────
function shareUrl() {
    const LZ = window.LZString;
    if (!LZ) return location.href;
    return location.origin + location.pathname + '#m=' + LZ.compressToEncodedURIComponent(snapshot());
}
function fromHash() {
    const m = location.hash.match(/#m=([^&]+)/);
    if (!m || !window.LZString) return false;
    try {
        const s = window.LZString.decompressFromEncodedURIComponent(m[1]);
        if (!s) return false;
        S = Object.assign(freshState(), E.upgradeChartState(JSON.parse(s)));
        return true;
    } catch (e) { return false; }
}
function contentKey() {
    // One map = one unit of the daily cap: the same map exported again (another size,
    // another format) is free. Export settings are not part of the key.
    const s = snapshot();
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return 'map:' + (h >>> 0).toString(36);
}

// ── export ───────────────────────────────────────────────────────────────
function exportSvgText() {
    const svg = $('#mc-svg').cloneNode(true);
    const z = svg.querySelector('#mc-zoom');
    if (z) z.removeAttribute('transform');
    const c = svg.querySelector('.mc-c');
    if (c) c.setAttribute('stroke-width', '0.6');
    svg.setAttribute('xmlns', svgNS);
    const vb = $('#mc-svg').viewBox.baseVal;
    svg.setAttribute('width', String(vb.width)); svg.setAttribute('height', String(vb.height));
    svg.querySelectorAll('title').forEach((t) => t.remove());
    return '<?xml version="1.0" encoding="UTF-8"?>\n' + new XMLSerializer().serializeToString(svg);
}
function fileBase() {
    const t = (S.style.title || tr('file_default')).trim();
    try { return window.__outputName ? window.__outputName.sanitize(t) : t.replace(/[\\/:*?"<>|]+/g, '_'); } catch (e) { return 'map'; }
}
async function doExport(fmt) {
    const run = window.__toolLimit ? window.__toolLimit.begin(SLUG, contentKey()) : null;
    if (window.__toolLimit && !run) return;
    stage('export', { fmt });
    try {
        let ok = false;
        if (fmt === 'svg') {
            ok = await window.__downloadFile(new Blob([exportSvgText()], { type: 'image/svg+xml' }), fileBase() + '.svg');
        } else if (fmt === 'pdf') {
            const mod = await import('./pdf.js');
            const blob = await mod.mapPdf($('#mc-svg'), { title: S.style.title, paper: $('#mc-paper').value, locale: LANG, assets: W.assets || {} });
            ok = await window.__downloadFile(blob, fileBase() + '.pdf');
        } else {
            const side = +$('#mc-size').value || 2000;
            const blob = await rasterize(side);
            ok = await window.__downloadFile(blob, fileBase() + '.png');
        }
        if (ok === false) { run && run.abort(); return; }
        run && run.commit();
        if (drafts) drafts.exported();
        try { window.__aeToolComplete && window.__aeToolComplete({ fmt, view: S.view.startsWith('reg:') ? 'reg' : S.view, mode: S.mode, n: S.mode === 'groups' ? Object.keys(S.assign).length : (dataView ? dataView.values.size : 0) }); } catch (e) { /* optional */ }
    } catch (e) {
        run && run.abort();
        try { window.__handleError ? window.__handleError(e, 'map_export', { say: tr('export_fail') }) : toast(tr('export_fail'), 'error'); } catch (_) { toast(tr('export_fail'), 'error'); }
    }
}
async function rasterize(side) {
    const vb = $('#mc-svg').viewBox.baseVal;
    return rasterizeSvg(exportSvgText(), vb.width, vb.height, side, (got) => {
        toast(tr('png_clamped', { px: got }), 'info');
        ae('png_clamped', { want: side, got });
    });
}

// ── boot ─────────────────────────────────────────────────────────────────
function bind() {
    $$('.mc-views .tc-chip').forEach((b) => b.addEventListener('click', () => {
        const v = b.dataset.view;
        if (v === 'reg') { $('#mc-reg-wrap').hidden = false; setView('reg:' + ($('#mc-reg').value || W.defaultRegion)); }
        else setView(v);
    }));
    $('#mc-reg').addEventListener('change', (e) => setView('reg:' + e.target.value));
    $('#mc-proj').addEventListener('change', (e) => { S.proj = e.target.value; geoCache.key = ''; Z.k = 1; Z.x = 0; Z.y = 0; render(); commit(); ae('proj', { p: S.proj }); });
    $$('.mc-modes .tc-seg-b').forEach((b) => b.addEventListener('click', () => { S.mode = b.dataset.mode; syncControls(); render(); commit(); ae('mode', { mode: S.mode }); }));
    // groups
    const gl = $('#mc-groups');
    gl.addEventListener('click', (e) => {
        const del = e.target.closest('[data-del]'), row = e.target.closest('.mc-grp');
        if (row && !del && +row.dataset.i !== S.active) setActive(+row.dataset.i);
        if (del) {
            const i = +del.dataset.del;
            if (S.groups.length < 2) return;
            S.groups.splice(i, 1);
            for (const [id, g] of Object.entries(S.assign)) { if (g === i) delete S.assign[id]; else if (g > i) S.assign[id] = g - 1; }
            S.active = Math.min(S.active, S.groups.length - 1);
            render(); commit();
        }
    });
    gl.addEventListener('input', (e) => {
        const c = e.target.closest('[data-color]'), l = e.target.closest('[data-label]');
        if (c) { S.groups[+c.dataset.color].c = c.value; render(); }
        if (l) { S.groups[+l.dataset.label].l = l.value; render(); }
    });
    gl.addEventListener('change', () => commit());
    gl.addEventListener('focusin', (e) => { const row = e.target.closest('.mc-grp'); if (row) setActive(+row.dataset.i); });
    $('#mc-quick').addEventListener('click', (e) => { const b = e.target.closest('[data-q]'); if (b) { setActive(+b.dataset.q); ae('quick'); } });
    $('#mc-set').addEventListener('change', (e) => { const k = e.target.value; e.target.value = ''; if (k) applySet(k); });
    $('#mc-add-group').addEventListener('click', () => {
        if (S.groups.length >= 10) { toast(tr('group_max'), 'info'); return; }
        S.groups.push({ c: E.QUALITATIVE[S.groups.length % E.QUALITATIVE.length], l: tr('group_n', { n: S.groups.length + 1 }) });
        S.active = S.groups.length - 1; render(); commit();
    });
    const find = $('#mc-find');
    find.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const id = matcher && matcher(find.value);
        if (!id || !geoCache.paths.has(id)) { toast(tr('find_none', { q: find.value }), 'info'); diag('find_miss', { q: find.value.trim().slice(0, 40), view: S.view.startsWith('reg:') ? 'reg' : S.view }); return; }
        S.assign[id] = S.active; commit(); render(); find.value = ''; ae('find'); firstEdit('find');
    });
    $('#mc-clear').addEventListener('click', () => { if (!Object.keys(S.assign).length && !S.data.text) return; S.assign = {}; S.data.text = ''; syncControls(); render(); commit(); });
    // data
    let dt = 0;
    $('#mc-data-text').addEventListener('input', (e) => { S.data.text = e.target.value; clearTimeout(dt); dt = setTimeout(() => { render(); commit(); firstEdit('data'); }, 300); });
    $('#mc-method').addEventListener('change', (e) => { S.data.method = e.target.value; render(); commit(); });
    $('#mc-k').addEventListener('change', (e) => { S.data.k = +e.target.value; render(); commit(); });
    $('#mc-rev').addEventListener('change', (e) => { S.data.rev = e.target.checked; render(); commit(); });
    $$('.mc-ramp').forEach((b) => b.addEventListener('click', () => { S.data.ramp = b.dataset.ramp; syncControls(); render(); commit(); }));
    $('#mc-sample').addEventListener('click', () => { S.data.text = W.sample || ''; syncControls(); render(); commit(); ae('sample'); });
    // style
    const st = (id, key, ev = 'input', cast = (x) => x) => $(id).addEventListener(ev, (e) => { S.style[key] = cast(e.target.type === 'checkbox' ? e.target.checked : e.target.value); render(); });
    st('#mc-title', 'title'); st('#mc-note', 'note'); st('#mc-legend-title', 'legendTitle');
    st('#mc-labels', 'labels', 'change'); st('#mc-grat', 'grat', 'change');
    st('#mc-ocean', 'ocean'); st('#mc-land', 'land'); st('#mc-border', 'border'); st('#mc-legend', 'legend', 'change');
    ['#mc-title', '#mc-note', '#mc-legend-title', '#mc-ocean', '#mc-land', '#mc-border'].forEach((s) => $(s).addEventListener('change', () => commit()));
    ['#mc-labels', '#mc-grat', '#mc-legend'].forEach((s) => $(s).addEventListener('change', () => commit()));
    // history, share, export
    $('#mc-undo').addEventListener('click', undo);
    $('#mc-redo').addEventListener('click', redo);
    document.addEventListener('keydown', (e) => {
        if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(e)) return;
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? redo() : undo(); }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') { e.preventDefault(); redo(); }
    });
    $('#mc-share').addEventListener('click', () => {
        const url = shareUrl();
        ae('share', { how: navigator.share && window.matchMedia('(pointer:coarse)').matches ? 'sheet' : 'copy' });
        // A phone hands the link to a messenger through the system sheet; a computer copies it.
        // No clipboard (an old WebView) — the link in a prompt to copy by hand, never silence.
        if (navigator.share && window.matchMedia('(pointer:coarse)').matches) { navigator.share({ title: document.title, url }).catch(() => { /* closed */ }); return; }
        const byHand = () => { try { window.prompt(tr('link_label'), url); } catch (e) { toast(tr('copy_fail'), 'error'); } };
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(() => toast(tr('link_copied'), 'success'), byHand);
        else byHand();
    });
    $$('[data-export]').forEach((b) => b.addEventListener('click', () => doExport(b.dataset.export)));
    bindZoom();
}

function draftBoot() {
    if (!window.__toolDraft || !window.__toolDraft.editor) return;
    drafts = window.__toolDraft.editor({
        slug: SLUG, root: 'mcDrafts', icon: '🗺️', locale: LANG, debounceMs: 600,
        payload: () => (feats.length ? { state: S, label: S.style.title || (S.view.startsWith('reg:') ? tr('view_reg') : tr('view_' + S.view)) } : null),
        apply: (d) => { S = Object.assign(freshState(), E.upgradeChartState(d.state)); syncControls(); return loadView().then((fresh) => { if (fresh) render(); hist.stack = [snapshot()]; hist.pos = 0; syncUndo(); return []; }); },
        fresh: () => { S = freshState(); syncControls(); loadView().then((fresh) => { if (fresh) render(); hist.stack = [snapshot()]; hist.pos = 0; syncUndo(); }); },
        started: () => hist.pos > 0,
        t: (k, f) => (I['drafts_' + k] != null ? I['drafts_' + k] : f),
        track: (a, d) => ae('draft_' + a, d), toast,
    });
    return drafts.boot();
}

async function boot() {
    if (!d3) { toast(tr('load_fail'), 'error'); return; }
    const linked = fromHash();
    bind();
    syncControls();
    await loadView();
    syncProjOptions();
    render();
    hist.stack = [snapshot()]; hist.pos = 0; syncUndo();
    draftBoot();
    if (linked && drafts && drafts.adopt) drafts.adopt(true);
    stage('ready', { ms: Math.round(performance.now()), linked: linked ? 1 : 0, n: feats.length });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
